/**
 * /me/inquiries/:leadId/accept — Breeder accepts a Verified Inquiry.
 *
 * Triggered from the email link in the inquiry-received email. On
 * mount, POSTs to /me/inquiries/:leadId/accept which fires the
 * 3-way auto-intro: buyer + breeder both get a structured email
 * with kennel standards, suggested first questions, and an audit
 * reference ID.
 *
 * Idempotent — re-visiting shows "already accepted" instead of
 * re-sending.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, WarningIcon as Warning, HandshakeIcon as Handshake } from "@phosphor-icons/react";

export default function AcceptInquiry() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState({ loading: true, ok: false, err: null, data: null });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=/me/inquiries/${leadId}/accept`);
      return;
    }
    api.post(`/me/kennel/inquiries/${leadId}/accept`)
      .then((r) => setState({ loading: false, ok: true, err: null, data: r.data }))
      .catch((e) => setState({
        loading: false, ok: false,
        err: e?.response?.data?.detail || "Could not accept inquiry.",
        data: null,
      }));
  }, [authLoading, user, leadId, navigate]);

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="accept-inquiry-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Verified Inquiry
        </p>
        <h1 className="font-spectral font-light text-4xl tracking-tight leading-[1.05] flex items-center gap-3">
          <Handshake size={28} weight="duotone" className="text-gold"/>
          Accept this inquiry
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-xl leading-relaxed">
          Accepting fires a 3-way intro email to the buyer with your kennel
          standards, your contact info, and five suggested first questions —
          so the conversation starts with useful context, not "tell me about
          your puppies."
        </p>

        {state.loading && (
          <div className="mt-8 rounded-xl border border-[#E5E5E5] bg-white p-5" data-testid="accept-inquiry-loading">
            <p className="text-sm text-[#6B6B6B]">Sending intro emails…</p>
          </div>
        )}

        {!state.loading && state.ok && (
          <div className="mt-8 rounded-xl border border-[#0F8043]/40 bg-[#EFF7EE] p-5 flex items-start gap-3" data-testid="accept-inquiry-ok">
            <CheckCircle size={22} weight="fill" className="text-[#0F8043] flex-shrink-0"/>
            <div className="text-[13.5px] leading-relaxed">
              <p className="font-semibold text-[#0F8043]">
                {state.data?.already_accepted ? "Already accepted." : "Intro sent."}
              </p>
              <p className="text-[#3F3F3F] mt-1">
                {state.data?.already_accepted
                  ? "You accepted this inquiry earlier. Both you and the buyer already received the structured intro email."
                  : "The buyer has been emailed your kennel standards, your contact info, and 5 suggested first questions. Their first reply lands directly in your inbox."}
              </p>
              <p className="text-[12px] text-[#6B6B6B] mt-2">
                Intro reference: <code className="font-tabular">#{state.data?.intro_id?.slice(0, 8)}</code>
              </p>
              <p className="text-[12px] text-[#6B6B6B] mt-1">
                Armstrong is out of the thread from here — we hold no money,
                and we don't host the negotiation.
              </p>
              <Link to="/dashboard/intros" className="inline-block mt-4 text-crimson underline font-bold text-[12px]" data-testid="accept-inquiry-feed-link">
                View all intros in your Buyer Activity Feed →
              </Link>
            </div>
          </div>
        )}

        {!state.loading && state.err && (
          <div className="mt-8 rounded-xl border border-crimson/40 bg-[#FBF1EE] p-5 flex items-start gap-3" data-testid="accept-inquiry-err">
            <Warning size={22} weight="fill" className="text-crimson flex-shrink-0"/>
            <div className="text-[13.5px] leading-relaxed">
              <p className="font-semibold text-crimson">Could not accept.</p>
              <p className="text-[#3F3F3F] mt-1">{state.err}</p>
              <p className="text-[12px] text-[#6B6B6B] mt-2">
                If this looks wrong, the inquiry may belong to a different
                kennel under your account, or it may have been removed by
                admin. You can still reply directly to the buyer's email
                from the original inquiry message.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="rounded-full mt-3"
              >
                Back to dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
